import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import VideoProxy from './VideoProxy';
import config from '../config';
import { handleVisibilityChange, diagnoseAndFixConnection } from '../lib/supabase';

// Usar un tipo genérico para la configuración de ReactPlayer
type ReactPlayerConfig = {
  [key: string]: any;
};

// Definición de las interfaces para la configuración
interface YouTubePlayerVars {
  modestbranding?: number;
  rel?: number;
  showinfo?: number;
  autoplay?: number;
  enablejsapi?: number;
  origin?: string;
}

interface YouTubeConfig {
  playerVars?: YouTubePlayerVars;
  onPlay?: () => void;
  onError?: (error: any) => void;
  onReady?: () => void;
}

interface FileAttributes {
  controlsList?: string;
  crossOrigin?: string;
}

interface FileConfig {
  attributes?: FileAttributes;
  forceVideo?: boolean;
  forceDuration?: boolean;
  forceHLS?: boolean;
  forceSafariHLS?: boolean;
  forceDisableHLS?: boolean;
  forceDASH?: boolean;
  forceFLV?: boolean;
  hlsOptions?: any;
  hlsVersion?: string;
  dashVersion?: string;
  flvVersion?: string;
  tracks?: {
    kind: string;
    src: string;
    srcLang: string;
    default?: boolean;
  }[];
}

interface VideoPlayerProps {
  url: string;
  playing: boolean;
  progress: number;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  onProgress: (state: { played: number; playedSeconds: number; loadedSeconds: number }) => void;
  onPlaybackRateChange?: (rate: number) => void;
  playerRef?: React.RefObject<ReactPlayer | null>;
}

const animationStyles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }
`;

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  playing,
  progress,
  onPlayPause,
  onSeek,
  onProgress,
  onPlaybackRateChange,
  playerRef,
}) => {
  const [duration, setDuration] = useState(0);
  const [hoveredTime, setHoveredTime] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [quality, setQuality] = useState('auto');
  const [subtitles, setSubtitles] = useState('off');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [actualUrl, setActualUrl] = useState<string>(url);
  const [useProxy, setUseProxy] = useState<boolean>(false);
  const [proxyError, setProxyError] = useState<string | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Re-agregar refs y estados faltantes
  const progressBarRef = useRef<HTMLDivElement>(null);
  const prevProgressRef = useRef<number>(progress);
  const containerRef = useRef<HTMLDivElement>(null);
  const isUserActionRef = useRef<boolean>(false);
  const [subtitlesAvailable, setSubtitlesAvailable] = useState(false);
  const [subtitlesLoading, setSubtitlesLoading] = useState(true);

  // Referencia para rastrear si hay una operación de visibilidad en curso
  const isVisibilityOperationInProgressRef = useRef<boolean>(false);

  // Usar playerRef interno si no se proporciona uno externo
  const internalPlayerRef = useRef<ReactPlayer>(null);
  const refToUse = playerRef || internalPlayerRef;

  // Sincronizar la posición del video cuando cambia externamente (por acción de otro usuario)
  useEffect(() => {
    // Solo realizar la búsqueda si el cambio viene de otro usuario
    if (Math.abs(progress - prevProgressRef.current) > 0.01 && !isUserActionRef.current) {
      // Añadir verificación para playerRef.current
      if (refToUse.current) {
        console.log(`Otro usuario movió el video a ${progress * 100}%`);
        refToUse.current.seekTo(progress, 'fraction');
      }
    }

    // Resetear el flag después de aplicar el cambio
    isUserActionRef.current = false;
    prevProgressRef.current = progress;
  }, [progress, duration, refToUse]);

  // Manejar pantalla completa
  const handleFullScreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error al intentar mostrar pantalla completa: ${err.message}`);
      });
      setIsFullScreen(true);

      // Si es un dispositivo móvil, intentar cambiar a orientación landscape (horizontal)
      if (isMobile && screen.orientation) {
        try {
          // Usar any para evitar errores de TypeScript
          const screenOrientation = screen.orientation as any;
          if (screenOrientation && typeof screenOrientation.lock === 'function') {
            screenOrientation.lock('landscape').catch((err: Error) => {
              console.warn("No se pudo bloquear la orientación: ", err);
              // Mostrar notificación de ayuda
              showNotification("Gira tu dispositivo para una mejor experiencia");
            });
          }
        } catch (err) {
          console.warn("API de orientación no soportada: ", err);
        }
      }

      // Notificar al usuario
      showNotification("Modo pantalla completa activado");
    } else {
      document.exitFullscreen().catch(err => {
        console.error(`Error al intentar salir de pantalla completa: ${err.message}`);
      });
      setIsFullScreen(false);

      // Si es un dispositivo móvil, liberar el bloqueo de orientación
      if (isMobile && screen.orientation) {
        try {
          // Usar any para evitar errores de TypeScript
          const screenOrientation = screen.orientation as any;
          if (screenOrientation && typeof screenOrientation.unlock === 'function') {
            screenOrientation.unlock();
          }
        } catch (err) {
          console.warn("Error al desbloquear orientación: ", err);
        }
      }

      // Notificar al usuario
      showNotification("Modo pantalla completa desactivado");
    }
  };

  // Función para mostrar notificaciones temporales
  const showNotification = (message: string) => {
    setNotification(message);

    // Limpiar notificación previa si existe
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    // Auto ocultar después de 3 segundos (solo en pantalla completa)
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(null);
    }, 3000);

    // Si no estamos en pantalla completa, emitir un evento personalizado para
    // que el componente Room pueda mostrar la notificación en el chat
    if (!isFullScreen) {
      const event = new CustomEvent('video-notification', {
        detail: { message: message }
      });
      document.dispatchEvent(event);
    }
  };

  // Limpiar el temporizador de notificación al desmontar
  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  // Escuchar cambios en el estado de pantalla completa
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Agregar escucha para notificaciones en pantalla completa
  useEffect(() => {
    // Solo configurar el listener si estamos en pantalla completa
    if (isFullScreen) {
      const handleFullscreenNotification = (event: any) => {
        // Mostrar la notificación usando nuestro sistema existente
        showNotification(event.detail.message);
      };

      document.addEventListener('fullscreen-notification', handleFullscreenNotification);

      return () => {
        document.removeEventListener('fullscreen-notification', handleFullscreenNotification);
      };
    }
  }, [isFullScreen]);

  const formatTime = (seconds: number) => {
    const pad = (num: number) => num.toString().padStart(2, '0');
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
  };

  const handleProgressBarClick = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

    // Marcar que este cambio viene del usuario actual
    isUserActionRef.current = true;

    // Aplicar inmediatamente en el player local
    if (refToUse.current) {
      refToUse.current.seekTo(percentage, 'fraction');
    }

    // Notificar el cambio para sincronizar con otros usuarios
    onSeek(percentage);
  };

  const handleProgressBarHover = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setHoveredTime(percentage * duration);
    setIsHovering(true);
  };

  const handleSeek = (time: number) => {
    // Marcar que este cambio viene del usuario actual
    isUserActionRef.current = true;

    const clampedTime = Math.max(0, Math.min(1, time));
    onSeek(clampedTime);
  };

  const handleProgress = (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
    // Usar requestAnimationFrame para optimizar el rendimiento
    requestAnimationFrame(() => {
      // Solo actualizar el progreso local, sin sincronizar
      // Esto permite que cada usuario tenga su propia reproducción fluida
      onProgress({
        played: state.played,
        playedSeconds: state.playedSeconds,
        loadedSeconds: state.loadedSeconds
      });
    });
  };

  const handlePlaybackRateChange = (rate: number) => {
    setPlaybackRate(rate);
    if (onPlaybackRateChange) {
      onPlaybackRateChange(rate);
    }
  };

  const handlePlayPauseClick = () => {
    // Marcar que este cambio viene del usuario actual
    isUserActionRef.current = true;

    // Notificar el cambio para sincronizar con otros usuarios
    onPlayPause();
  };

  // Control de volumen
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);

    // Si el volumen es 0, silenciamos
    // Si el volumen es > 0 y estaba silenciado, quitamos el silencio
    if (newVolume === 0 && !isMuted) {
      setIsMuted(true);
    } else if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);

    // Si desactivamos el silencio y el volumen era 0, lo restauramos a un valor razonable
    if (!newMutedState && volume === 0) {
      setVolume(0.7);
    }
  };

  // Calcular el volumen efectivo (considerando el estado del silencio)
  const effectiveVolume = isMuted ? 0 : volume;

  // Función para manejar cuando el reproductor está realmente listo
  const handleReady = () => {
    console.log("ReactPlayer está listo, iframe cargado correctamente");
    setPlayerReady(true);

    // Intentar acceder al iframe de YouTube para registrar eventos adicionales
    try {
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        if (iframe.src.includes('youtube.com')) {
          console.log("Detectado iframe de YouTube, intentando forzar reproducción");

          // Intentar forzar reproducción después de que el iframe esté listo
          if (playing) {
            setTimeout(() => {
              // Añadir verificación para playerRef.current
              if (refToUse.current) {
                console.log("Forzando reproducción tras 1 segundo");
                // Usar optional chaining (?.) para mayor seguridad
                refToUse.current.getInternalPlayer()?.pauseVideo?.();
                setTimeout(() => {
                  refToUse.current?.getInternalPlayer()?.playVideo?.();
                }, 100);
              }
            }, 1000);
          }
        }
      });
    } catch (error) {
      console.error("Error al intentar interactuar con iframe de YouTube:", error);
    }
  };

  // Detectar subtítulos disponibles
  useEffect(() => {
    if (!url) return;

    // Reset estado cuando cambia la URL
    setPlayerReady(false);

    const checkSubtitles = async () => {
      setSubtitlesLoading(true);
      try {
        // Para YouTube
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
          // Intentar obtener el ID del video
          const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
          if (videoIdMatch && videoIdMatch[1]) {
            const videoId = videoIdMatch[1];

            // Usar nuestro proxy en lugar de la API directamente
            const apiUrl = config.apiUrl; // Usar la URL del API desde la configuración
            const response = await fetch(`${apiUrl}/api/youtube/captions?videoId=${videoId}`);

            if (response.ok) {
              const data = await response.json();
              // Si hay items, hay subtítulos disponibles
              const hasSubtitles = data.items && data.items.length > 0;
              setSubtitlesAvailable(hasSubtitles);

              // Si hay error pero no es crítico, permitir continuar
              if (data.error) {
                console.warn('Advertencia al verificar subtítulos:', data.error);
              }

              // Incluso si hay un error, continuamos con la reproducción
            } else {
              console.warn('Respuesta no válida al verificar subtítulos:', response.status);
              // No bloquear la reproducción por esto
              setSubtitlesAvailable(false);
            }
          } else {
            console.warn('No se pudo extraer el ID del video de YouTube:', url);
            setSubtitlesAvailable(false);
          }
        } else {
          // Para otros proveedores, asumimos que pueden tener subtítulos
          setSubtitlesAvailable(true);
        }
      } catch (error) {
        console.error('Error al verificar subtítulos:', error);
        // En caso de error, asumir que no hay para no bloquear la reproducción
        setSubtitlesAvailable(false);
      } finally {
        setSubtitlesLoading(false);
      }
    };

    checkSubtitles();
  }, [url]);

  // Detectar si la URL es de una fuente de anime o embebida
  const isAnimeSource = (url: string): boolean => {
    const animeDomains = [
      // Sitios de anime
      'otakustv', 'otakus.tv', 'animeflv', 'jkanime', 'animefenix',
      'crunchyroll', 'funimation', 'aniplus', 'tioanime', 'animeblix', 'animeyt',

      // Servidores de streaming comunes
      'fembed', 'streamtape', 'streamwish', 'voe.sx', 'embedsito',
      'mp4upload', 'yourupload', 'mystream', 'streamsb', 'vidoza', 'vidlox',
      'mixdrop', 'uqload', 'upstream', 'vidcloud', 'videobin', 'videobb',
      'sbplay', 'watchsb', 'streamsb', 'sbvideo', 'ssbstream', 'sbfast'
    ];

    // Patrones de URLs que indican streaming embebido
    const embedPatterns = [
      'embed', 'player', 'video', 'stream', 'watch', 'play',
      'iframe', 'streaming', 'reproductor'
    ];

    // Verificar dominios conocidos
    if (animeDomains.some(domain => url.toLowerCase().includes(domain))) {
      return true;
    }

    // Verificar patrones comunes de embebido
    if (embedPatterns.some(pattern => url.toLowerCase().includes(pattern))) {
      // Excluir YouTube y Vimeo ya que tienen sus propios controles
      if (!url.includes('youtube.com') && !url.includes('youtu.be') &&
          !url.includes('vimeo.com')) {
        return true;
      }
    }

    // Verificar parámetros de calidad/servidor que pueden haberse añadido
    try {
      const urlObj = new URL(url);
      if (urlObj.searchParams.has('server') ||
          urlObj.searchParams.has('quality') ||
          urlObj.searchParams.has('source')) {
        return true;
      }
    } catch {
      // Si la URL no es válida, ignorar esta verificación
    }

    return false;
  };

  // Efecto para evaluar si la URL requiere proxy y manejar cambios de visibilidad
  useEffect(() => {
    if (!url) {
      setActualUrl('');
      return;
    }

    // Determinar si necesitamos usar el proxy
    const needsProxy = isAnimeSource(url) && !url.includes('youtube.com') && !url.includes('youtu.be');
    setUseProxy(needsProxy);

    // Si no necesita proxy, usar URL directamente
    if (!needsProxy) {
      setActualUrl(url);
    }
    // Si necesita proxy, la URL será establecida por el componente VideoProxy

    // Usar la referencia definida en el nivel superior

    // Función para manejar cambios de visibilidad de manera no bloqueante
    const handlePlayerVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Si ya hay una operación en curso, no iniciar otra
        if (isVisibilityOperationInProgressRef.current) {
          console.log("Ya hay una operación de visibilidad en curso para el reproductor, omitiendo.");
          return;
        }

        console.log('Reproductor: Documento visible nuevamente, verificando estado del reproductor...');

        // Marcar que hay una operación en curso
        isVisibilityOperationInProgressRef.current = true;

        // Establecer un tiempo límite para la operación completa
        const timeoutId = setTimeout(() => {
          console.log("Tiempo de espera agotado para la operación de visibilidad del reproductor.");
          isVisibilityOperationInProgressRef.current = false;
        }, 5000); // 5 segundos de tiempo límite

        // Ejecutar en un contexto no bloqueante
        setTimeout(async () => {
          try {
            // --- PASO 1: Diagnosticar y solucionar problemas de conexión ---
            console.log("Diagnosticando problemas de conexión para el reproductor...");
            const diagnosis = await diagnoseAndFixConnection();
            console.log("Resultado del diagnóstico para el reproductor:", diagnosis);

            // Si el cliente está en mal estado después del diagnóstico, recargar la página
            if (diagnosis.clientStatus === 'error') {
              console.log("Cliente en mal estado para el reproductor, recargando página...");
              window.location.reload();
              return;
            }

            // --- PASO 2: Ejecutar la función centralizada de reconexión ---
            console.log("Ejecutando proceso de reconexión centralizado para el reproductor...");
            await handleVisibilityChange();

            // --- PASO 3: Verificar si el reproductor está en un estado válido ---
            if (refToUse.current) {
              try {
                // Intentar acceder al reproductor interno para verificar su estado
                const internalPlayer = refToUse.current.getInternalPlayer();

                if (!internalPlayer) {
                  console.log('Reproductor: Reproductor interno no disponible, reiniciando...');
                  // Reiniciar el reproductor
                  setPlayerReady(false);
                  const currentUrl = actualUrl;
                  setActualUrl('');

                  // Usar requestAnimationFrame para asegurar que la UI se actualice correctamente
                  requestAnimationFrame(() => {
                    setTimeout(() => {
                      setActualUrl(currentUrl);
                    }, 100);
                  });
                } else {
                  console.log('Reproductor: Reproductor interno disponible, continuando reproducción normal');
                  // Si el reproductor está disponible y debería estar reproduciendo, asegurarse de que lo esté
                  if (playing && typeof internalPlayer.playVideo === 'function') {
                    internalPlayer.playVideo();
                  }
                }
              } catch (error) {
                console.error('Error al verificar estado del reproductor:', error);
                // En caso de error, reiniciar el reproductor
                setPlayerReady(false);
                const currentUrl = actualUrl;
                setActualUrl('');

                requestAnimationFrame(() => {
                  setTimeout(() => {
                    setActualUrl(currentUrl);
                  }, 100);
                });
              }
            }

            // --- PASO 4: Verificar si la reconexión fue exitosa ---
            const finalCheck = await diagnoseAndFixConnection();
            if (finalCheck.clientStatus === 'error' || finalCheck.sessionStatus === 'error') {
              console.log("La reconexión no fue completamente exitosa para el reproductor, recargando página...");
              window.location.reload();
              return;
            }
          } catch (err) {
            console.error("Error durante la verificación del reproductor:", err);
            // SOLUCIÓN RADICAL: Recargar la página si hay un error grave
            console.log("Error grave durante la reconexión del reproductor, recargando página...");
            window.location.reload();
          } finally {
            // Limpiar el timeout y marcar que la operación ha terminado
            clearTimeout(timeoutId);
            isVisibilityOperationInProgressRef.current = false;
          }
        }, 0); // Ejecutar en el siguiente ciclo de eventos
      }
    };

    // Agregar listener para cambios de visibilidad
    document.addEventListener('visibilitychange', handlePlayerVisibilityChange);

    // Limpiar al desmontar o cuando cambia la URL
    return () => {
      document.removeEventListener('visibilitychange', handlePlayerVisibilityChange);
    };
  }, [url, actualUrl, playing]);

  // Manejar cuando el proxy proporciona una URL
  const handleProxyUrl = (proxyUrl: string) => {
    setActualUrl(proxyUrl);
    setProxyError(null);
  };

  // Manejar errores del proxy
  const handleProxyError = (error: string) => {
    console.error("Error en proxy de video:", error);
    setProxyError(error);
    // Caer de nuevo a la URL original en caso de error
    setActualUrl(url);
  };

  // Función para mostrar/ocultar controles en dispositivos móviles
  const toggleControls = () => {
    // Usar requestAnimationFrame para evitar reflows forzados
    requestAnimationFrame(() => {
      setShowControls(prev => !prev);

      // Si los controles están visibles, configurar un temporizador para ocultarlos después de 3 segundos
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = null;
      }

      // Configurar temporizador solo si estamos mostrando los controles (tanto en PC como en móvil)
      if (!showControls) {
        controlsTimeoutRef.current = setTimeout(() => {
          requestAnimationFrame(() => {
            setShowControls(false);
          });
        }, 3000);
      }
    });
  };

  // Detectar si es un dispositivo móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640 || 'ontouchstart' in window);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Reiniciar el temporizador cuando el usuario interactúa con los controles
  const resetControlsTimeout = () => {
    // Aplicar para ambos: PC y móvil
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    // Usar setTimeout con tiempo mínimo
    controlsTimeoutRef.current = setTimeout(() => {
      // Usar requestAnimationFrame para evitar operaciones costosas durante el scroll
      requestAnimationFrame(() => {
        setShowControls(false);
      });
    }, 3000);
  };

  // Limpiar el temporizador al desmontar el componente
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // Controlar los eventos de toque para el contenedor del video
  const handleVideoTouch = React.useCallback((e: React.TouchEvent) => {
    // Prevenir que el evento se propague si ya estamos interactuando con los controles
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('select')) {
      return;
    }

    // Llamar a toggleControls directamente para mostrar/ocultar los controles
    toggleControls();

    // Solo prevenir el comportamiento por defecto dentro del área del reproductor
    // y solo para evitar dobles toques, no para bloquear scroll
    if (containerRef.current && containerRef.current.contains(target)) {
      // Prevenir comportamiento duplicado ya que onClick también se dispara en dispositivos táctiles
      // pero sin afectar a la funcionalidad de scroll
      e.stopPropagation();
    }
  }, [toggleControls]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Eliminar listener existente
    container.removeEventListener('touchstart', handleVideoTouch as unknown as EventListener);

    // Añadir con passive: true
    container.addEventListener('touchstart', handleVideoTouch as unknown as EventListener, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleVideoTouch as unknown as EventListener);
    };
  }, [handleVideoTouch]);

  useEffect(() => {
    // Agregar estilos de animación al cargar el componente
    const styleElement = document.createElement('style');
    styleElement.innerHTML = animationStyles;
    document.head.appendChild(styleElement);

    // Limpiar al desmontar
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Función mejorada para manejar errores del reproductor
  const handlePlayerError = (error: any) => {
    console.error("Error en el reproductor:", error);

    // Mostrar notificación para informar al usuario
    showNotification("Error en la reproducción. Reconectando...");

    // Intentar recuperar la reproducción para cualquier tipo de URL
    const player = refToUse.current?.getInternalPlayer();
    const isYouTube = actualUrl.includes('youtube.com') || actualUrl.includes('youtu.be');

    // Estrategia 1: Intentar recuperar directamente si el player está disponible
    if (player) {
      try {
        console.log("Intentando recuperar reproductor existente...");

        // Para YouTube
        if (isYouTube && typeof player.pauseVideo === 'function') {
          player.pauseVideo();

          // Usar requestAnimationFrame para programar la operación de manera más eficiente
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (player && typeof player.playVideo === 'function') {
                player.playVideo();
              }
            }, 500);
          });
          return; // Salir si la recuperación directa parece funcionar
        }
        // Para reproductores HTML5 estándar
        else if (player.play && typeof player.play === 'function') {
          player.currentTime = 0;
          player.play().catch(e => {
            console.error("Error al intentar reproducir video HTML5:", e);
            // Continuar con la estrategia 2 si falla
            recreatePlayer();
          });
          return; // Salir si la recuperación directa parece funcionar
        }
      } catch (e) {
        console.error("Error durante la recuperación directa del reproductor:", e);
        // Continuar con la estrategia 2
      }
    }

    // Estrategia 2: Recrear completamente el reproductor
    recreatePlayer();

    // Función para recrear el reproductor
    function recreatePlayer() {
      console.log("Recreando el reproductor completamente...");

      // Comprobar si seguimos en la misma URL para evitar conflictos
      if (actualUrl === url || !actualUrl) {
        // Limpiar el estado actual
        setPlayerReady(false);
        const currentUrl = url; // Usar la URL original
        setActualUrl("");

        // Usar requestAnimationFrame para la actualización de UI
        requestAnimationFrame(() => {
          setTimeout(() => {
            // Restaurar la URL para recrear el reproductor
            setActualUrl(currentUrl);

            // Notificar que estamos intentando reiniciar
            const event = new CustomEvent('video-notification', {
              detail: { message: "Reiniciando reproductor..." }
            });
            document.dispatchEvent(event);
          }, 200); // Dar un poco más de tiempo para que el DOM se actualice
        });
      }
    }
  };

  // Función auxiliar para extraer el ID de YouTube
  const extractYoutubeId = (url: string): string | null => {
    if (!url) return null;

    const regExp = /^.*(youtu.be\/|v\/|e\/|u\/\w+\/|embed\/|v=)([^#\&\?]*).*/;
    const match = url.match(regExp);

    return (match && match[2].length === 11) ? match[2] : null;
  };

  return (
    <div
      ref={containerRef}
      className="group relative w-full bg-black overflow-hidden rounded-lg"
      onClick={toggleControls}
      onTouchStart={handleVideoTouch}
    >
      {/* Contenido del proxy o mensaje de error */}
      {useProxy ? (
        proxyError ? (
          <div className="w-full aspect-video bg-gray-900 flex items-center justify-center rounded-lg text-white">
            <div className="text-center p-4">
              <p className="text-red-500 font-semibold mb-2">Error al cargar el video</p>
              <p className="text-sm opacity-80">{proxyError}</p>
              <button
                onClick={() => setUseProxy(false)}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm"
              >
                Volver a intentar con reproductor normal
              </button>
            </div>
          </div>
        ) : (
          <VideoProxy
            url={actualUrl}
            onError={handleProxyError}
            onProxyUrl={handleProxyUrl}
          />
        )
      ) : (
        /* Reproductor de video principal */
        <div className="w-full h-full" style={{ pointerEvents: 'none' }}>
          <ReactPlayer
            ref={refToUse}
            url={actualUrl}
            width="100%"
            height="100%"
            playing={playing}
            volume={effectiveVolume}
            muted={isMuted}
            playbackRate={playbackRate}
            progressInterval={1000}
            onDuration={setDuration}
            onProgress={handleProgress}
            onReady={handleReady}
            onClick={toggleControls}
            onClickPreview={toggleControls}
            onError={handlePlayerError}
            className="react-player"
            style={{ aspectRatio: '16 / 9' }}
            config={{
              youtube: {
                playerVars: {
                  modestbranding: 1,
                  rel: 0,
                  showinfo: 0,
                  autoplay: playing ? 1 : 0,
                  enablejsapi: 1,
                  origin: window.location.origin,
                  controls: 0,  // Deshabilitar controles nativos de YouTube
                  disablekb: 1, // Deshabilitar atajos de teclado de YouTube
                  fs: 0,        // Deshabilitar botón de pantalla completa de YouTube
                  iv_load_policy: 3, // Ocultar anotaciones
                }
              },
              file: {
                forceVideo: true,
                attributes: {
                  controlsList: 'nodownload',
                  className: 'w-full h-full'
                },
              }
            }}
          />
        </div>
      )}

      {/* Instrucción inicial para móviles que desaparece después de la primera interacción */}
      {isMobile && !localStorage.getItem('instructionShown') && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/70 z-10"
          onClick={() => {
            localStorage.setItem('instructionShown', 'true');
            toggleControls();
          }}
        >
          <div className="text-center p-6 max-w-xs">
            <svg className="w-12 h-12 mx-auto mb-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h10M7 16h10" />
            </svg>
            <p className="text-white text-sm mb-2">Toca la pantalla para mostrar los controles</p>
            <p className="text-gray-400 text-xs">Toca nuevamente para ocultarlos</p>
            <button
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm"
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem('instructionShown', 'true');
                toggleControls();
              }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Overlay para controles */}
      <div
        className={`absolute bottom-0 left-0 right-0 p-2 sm:p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
        onClick={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
      >
        {/* Barra de progreso */}
        <div
          ref={progressBarRef}
          className="relative h-2 mb-2 sm:mb-4 bg-gray-600/50 rounded-full cursor-pointer group/progress touch-none"
          onClick={(e) => {
            handleProgressBarClick(e);
            resetControlsTimeout();
          }}
          onTouchStart={(e) => {
            handleProgressBarClick(e);
            resetControlsTimeout();
          }}
          onTouchMove={(e) => {
            handleProgressBarHover(e);
            resetControlsTimeout();
          }}
          onMouseMove={handleProgressBarHover}
          onMouseLeave={() => setIsHovering(false)}
          onTouchEnd={() => setIsHovering(false)}
        >
          <div
            className="absolute h-full bg-blue-500 rounded-full"
            style={{ width: `${progress * 100}%` }}
          />
          <div
            className="absolute h-full bg-blue-400/30 rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"
            style={{ width: `${(hoveredTime / duration) * 100}%` }}
          />
          {isHovering && (
            <div
              className="absolute bottom-6 bg-black/90 text-white px-2 py-1 rounded text-sm transform -translate-x-1/2 border border-gray-700/50"
              style={{ left: `${(hoveredTime / duration) * 100}%` }}
            >
              {formatTime(hoveredTime)}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          {/* Controles izquierdos */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={(e) => {
                handlePlayPauseClick();
                resetControlsTimeout();
                e.stopPropagation();
              }}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500/20 hover:bg-blue-500/30 flex items-center justify-center transition-colors group/button touch-none"
            >
              {playing ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 group-hover/button:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                </svg>
              ) : (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 group-hover/button:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                </svg>
              )}
            </button>

            {/* Control de volumen */}
            <div
              className="relative"
              onMouseEnter={() => setShowVolumeControl(true)}
              onMouseLeave={() => setShowVolumeControl(false)}
            >
              <button
                onClick={(e) => {
                  toggleMute();
                  resetControlsTimeout();
                  e.stopPropagation();
                }}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hover:bg-blue-500/20 flex items-center justify-center transition-colors touch-none"
                title={isMuted ? "Activar sonido" : "Silenciar"}
              >
                {!isMuted ? (
                  volume > 0.6 ? (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </svg>
                  ) : volume > 0.2 ? (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    </svg>
                  )
                ) : (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                )}
              </button>

              {showVolumeControl && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-3 rounded-lg shadow-lg z-50"
                     style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)', border: '1px solid rgba(59, 130, 246, 0.2)', width: '80px' }}>
                  <div className="flex flex-col items-center">
                    <div className="h-20 flex flex-col items-center justify-center relative">
                      <div className="w-1 h-full bg-gray-700 rounded-full absolute">
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={handleVolumeChange}
                        className="volume-slider-vertical appearance-none bg-transparent z-10"
                        style={{
                          width: '60px',
                          height: '20px',
                          transform: 'rotate(-90deg) translateX(-20px)',
                          transformOrigin: 'left center'
                        }}
                      />
                      <div
                        className="w-1 bg-blue-500 rounded-full absolute bottom-0 left-0 right-0 mx-auto pointer-events-none"
                        style={{
                          height: `${volume * 100}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-xs mt-2 text-white">
                      {Math.round(volume * 100)}%
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Tiempo actual / Duración */}
            <div className="text-xs sm:text-sm text-gray-300">
              <span>{formatTime(progress * duration)}</span>
              <span className="mx-1">/</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controles derechos */}
          <div className="flex items-center space-x-2">
            {/* Botón de pantalla completa */}
            <button
              onClick={(e) => {
                handleFullScreen();
                resetControlsTimeout();
                e.stopPropagation();
              }}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hover:bg-blue-500/20 flex items-center justify-center transition-colors touch-none"
              title={isFullScreen ? "Salir de pantalla completa" : "Pantalla completa"}
            >
              {isFullScreen ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9L4 4m0 0l5 0m-5 0v5M9 15l-5 5m0 0l5 0m-5 0v-5M15 9l5-5m0 0h-5m5 0l-5 5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              ) : (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>

            {/* Botón de ajustes */}
            <div className="relative">
              <button
                onClick={(e) => {
                  setShowSettings(!showSettings);
                  resetControlsTimeout();
                  e.stopPropagation();
                }}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hover:bg-blue-500/20 flex items-center justify-center transition-colors touch-none"
                title="Configuración"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              {showSettings && (
                <div className="absolute bottom-full right-0 mb-2 p-3 sm:p-4 bg-black/90 rounded-lg w-56 sm:w-64 shadow-lg border border-gray-700/50 z-50">
                  {/* Velocidad de reproducción */}
                  <div className="mb-3 sm:mb-4">
                    <label className="block text-xs sm:text-sm text-gray-300 mb-1 sm:mb-2">Velocidad</label>
                    <select
                      value={playbackRate}
                      onChange={(e) => handlePlaybackRateChange(parseFloat(e.target.value))}
                      className="w-full bg-gray-800 text-white rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 touch-none"
                    >
                      <option value="0.25">0.25x</option>
                      <option value="0.5">0.5x</option>
                      <option value="0.75">0.75x</option>
                      <option value="1">1x</option>
                      <option value="1.25">1.25x</option>
                      <option value="1.5">1.5x</option>
                      <option value="2">2x</option>
                    </select>
                  </div>

                  {/* Calidad */}
                  <div className="mb-3 sm:mb-4">
                    <label className="block text-xs sm:text-sm text-gray-300 mb-1 sm:mb-2">Calidad</label>
                    <select
                      value={quality}
                      onChange={(e) => setQuality(e.target.value)}
                      className="w-full bg-gray-800 text-white rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 touch-none"
                    >
                      <option value="auto">Automática</option>
                      <option value="1080p">1080p</option>
                      <option value="720p">720p</option>
                      <option value="480p">480p</option>
                      <option value="360p">360p</option>
                    </select>
                  </div>

                  {/* Subtítulos */}
                  {(subtitlesAvailable || subtitlesLoading) && (
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-300 mb-1 sm:mb-2">
                        Subtítulos {subtitlesLoading && '(verificando...)'}
                      </label>
                      <select
                        value={subtitles}
                        onChange={(e) => setSubtitles(e.target.value)}
                        className="w-full bg-gray-800 text-white rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 touch-none"
                        disabled={!subtitlesAvailable || subtitlesLoading}
                      >
                        <option value="off">Desactivados</option>
                        <option value="es">Español</option>
                        <option value="en">Inglés</option>
                        <option value="fr">Francés</option>
                        <option value="de">Alemán</option>
                        <option value="auto">Auto (CC)</option>
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sistema de notificaciones en pantalla completa */}
      {isFullScreen && notification && (
        <div className="absolute bottom-16 right-4 max-w-xs z-50 animate-fadeIn">
          <div className="bg-black/70 text-white px-4 py-2 rounded-lg border border-blue-500/30 shadow-lg backdrop-blur-sm">
            <p className="text-sm font-medium">{notification}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;